import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Modal,
    TextField,
    CircularProgress,
    IconButton,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../../axiosClient';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import MonthYearSelector from './monthYearSelector';
import { currentPoolMonth } from '../../utils/commonUtils';
import ConfigureFieldAliasModal from '../modals/configureFieldAliasModal';
import { FieldAlias } from '../../interfaces/field';
import ErrorDisplay from './errorDisplay';
import { processExcelDataWithValidation } from '../../utils/uploadValidationUtils';
import FieldAliasService from '../../services/fieldAliasService';
import { parseErrorResponse, UploadError } from '../../utils/fileUtils';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    overflow: 'auto'
};

interface UploadProp {
    label: string;
    uploadApi: string;
    uploadType: string;
    uploadFormat?: string;
    open: boolean;
    onClose: () => void;
}

interface ValidationState {
    headers: string[];
    showFieldAliasModal: boolean;
    missingPrimaryFields: string[];
    unmappedNonPrimaryFields: string[];
    primaryFieldsValid: boolean;
    validationError: any | null;
}


const UploadCaseFileComponent: React.FC<UploadProp> = ({ label, uploadApi, uploadType, uploadFormat, open, onClose }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<UploadError | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [poolMonth, setPoolMonth] = useState<string>(currentPoolMonth());
    const [csvDownloadData, setCsvDownloadData] = useState<{ filename: string; data: Blob } | null>(null);
    const [selectedLoanType, setSelectedLoanType] = useState<string>('');
    const [loanTypes, setLoanTypes] = useState<string[]>([]);
    const [fieldAliases, setFieldAliases] = useState<FieldAlias[]>([]);
    
    // Validation state
    const [validationState, setValidationState] = useState<ValidationState>({
        headers: [],
        showFieldAliasModal: false,
        missingPrimaryFields: [],
        unmappedNonPrimaryFields: [],
        primaryFieldsValid: false,
        validationError: null
    });
    
    const [isValidating, setIsValidating] = useState(false);

    // Redux selectors
    const allFields = useSelector((state: RootState) => state.fields.fields);
    const picklists = useSelector((state: RootState) => state.picklists.picklists);

    useEffect(() => {
        setSuccessMessage(null);
        setCsvDownloadData(null);
        setError(null);
        
        // Load loan types from picklist when modal opens
        if (open) {
            const availableLoanTypes = picklists.LOAN_TYPE || [];
            setLoanTypes(availableLoanTypes);
        }
    }, [open, picklists]);

    // Load field aliases when loan type changes
    useEffect(() => {
        if (selectedLoanType && open) {
            loadFieldAliasesForLoanType();
        }
    }, [selectedLoanType, open]);

    const loadFieldAliasesForLoanType = async () => {
        if (!selectedLoanType) return;
        
        try {
            const fieldAliasesData = await FieldAliasService.getFieldAliases(selectedLoanType);
            setFieldAliases(fieldAliasesData);
        } catch (error) {
            console.error('Error loading field aliases:', error);
            setFieldAliases([]);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            // Reset validation state
            setValidationState({
                headers: [],
                showFieldAliasModal: false,
                missingPrimaryFields: [],
                unmappedNonPrimaryFields: [],
                primaryFieldsValid: false,
                validationError: null
            });
            setCsvDownloadData(null);
            setError(null);
            setSuccessMessage(null);
        }
    };

    const validatePrimaryFields = async () => {
        if (!selectedFile) {
            setError({
                type: 'Validation',
                message: 'Please select a file before validating.',
                details: []
            });
            return;
        }
        
        if (!selectedLoanType) {
            setError({
                type: 'Validation',
                message: 'Please select a loan type before validating.',
                details: []
            });
            return;
        }

        setIsValidating(true);
        setError(null);
        setValidationState(prev => ({ ...prev, validationError: null }));
        
        try {
            // Process Excel file with proper validation (requires Sheet1)
            const validation = await processExcelDataWithValidation(selectedFile);
            
            if (!validation.isValid) {
                setError({
                    type: 'File',
                    message: 'File validation failed',
                    details: validation.errors
                });
                setIsValidating(false);
                return;
            }

            const data = validation.data || [];
            // Extract headers and trim for frontend comparison (backend gets original file)
            const rawHeaders = data[0] || [];
            const headers = rawHeaders
                .map(header => header != null ? String(header).trim() : '')
                .filter(header => header !== '');
            
            // 1. First check if field aliases exist for the loan type
            if (fieldAliases.length === 0) {
                setValidationState(prev => ({
                    ...prev,
                    headers: headers,
                    missingPrimaryFields: [],
                    unmappedNonPrimaryFields: [],
                    primaryFieldsValid: false,
                }));
                setError({
                    type: 'Configuration',
                    message: `No field aliases found for loan type '${selectedLoanType}'. Cannot validate file.`,
                    details: [`Please configure field aliases first to map your Excel headers to required fields.`]
                });
                setIsValidating(false);
                return;
            }
            
            
            const uploadedHeaders = new Set(headers);
            
            if (uploadType === "BulkUpdate") {
                // For bulk update, only check if client_reference_id field alias exists in headers
                const clientRefFieldAlias = fieldAliases.find(alias => 
                    alias.field_name === 'client_reference_id'
                );
                if (!clientRefFieldAlias){
                    setValidationState(prev => ({
                        ...prev,
                        headers: headers,
                        missingPrimaryFields: [],
                        unmappedNonPrimaryFields: [],
                        primaryFieldsValid: false,
                    }));
                    setError({
                        type: 'Validation',
                        message: 'missing field alias for client_reference_id',
                        details: ['missing field alias for client_reference_id']
                    });
                    setIsValidating(false);
                    return;
                }
                
                if (!uploadedHeaders.has(clientRefFieldAlias.alias_name)) {
                    setValidationState(prev => ({
                        ...prev,
                        headers: headers,
                        missingPrimaryFields: [clientRefFieldAlias.alias_name],
                        unmappedNonPrimaryFields: [],
                        primaryFieldsValid: false,
                    }));
                    setError({
                        type: 'Validation',
                        message: `Missing required header: ${clientRefFieldAlias.alias_name}`,
                        details: [`Your Excel file must contain the '${clientRefFieldAlias.alias_name}' column header.`]
                    });
                    setIsValidating(false);
                    return;
                }
            } else {
                // For case upload, check all primary field aliases
                const primaryFieldAliases = fieldAliases.filter(alias => {
                    const field = allFields.find(f => f.field_name === alias.field_name);
                    return field && field.is_standard && field.field_type === 'PRIMARY';
                });
                
                const missingPrimaryFieldAliases = primaryFieldAliases.filter(alias => 
                    !uploadedHeaders.has(alias.alias_name)
                );
                    
                if (missingPrimaryFieldAliases.length > 0) {
                    const missingAliases = missingPrimaryFieldAliases.map(alias => alias.alias_name);
                    setValidationState(prev => ({
                        ...prev,
                        headers: headers,
                        missingPrimaryFields: missingAliases,
                        unmappedNonPrimaryFields: [],
                        primaryFieldsValid: false,
                    }));
                    setError({
                        type: 'Validation',
                        message: `Missing required field aliases in uploaded file: ${missingAliases.join(', ')}.`,
                        details: [`Please ensure your Excel file contains these column headers, or configure field aliases to match your headers.`]
                    });
                    setIsValidating(false);
                    return;
                }
            }
            
            // Common logic for both: Check all headers which don't have any field alias (give warnings)
            const allFieldAliases = new Set(fieldAliases.map(alias => alias.alias_name));
            const unmappedHeaders = headers.filter(header => !allFieldAliases.has(header));
            
            // Success: validation passed
            setValidationState(prev => ({
                ...prev,
                headers: headers,
                missingPrimaryFields: [],
                unmappedNonPrimaryFields: unmappedHeaders,
                primaryFieldsValid: true,
                validationError: null
            }));
            setSuccessMessage('File validation passed! Ready to upload.');
            
        } catch (error: any) {
            setError({
                type: 'File',
                message: 'File validation error',
                details: error.message || 'Unknown error occurred'
            });
        } finally {
            setIsValidating(false);
        }
    };

    const handleActualUpload = async () => {
        if (!validationState.primaryFieldsValid || !selectedFile) {
            setError({
                type: 'Validation',
                message: 'Please complete validation before uploading.',
                details: []
            });
            return;
        }

        if (uploadType === "PaymentFile" && !poolMonth) {
            setError({
                type: 'Validation',
                message: 'Please select a pool month.',
                details: []
            });
            return;
        }

        setIsUploading(true);
        setError(null);
        setCsvDownloadData(null);
        setValidationState(prev => ({ ...prev, validationError: null }));

        try {
            // Send original file directly to backend (no recreation needed)
            const formData = new FormData();
            formData.append('file', selectedFile, selectedFile.name);

            let apiUrl = uploadApi;
            
            if (uploadType === "CaseUpload") {
                apiUrl += `?loan_type=${selectedLoanType}`;
            } else if (uploadType === "BulkUpdate") {
                apiUrl += `?loan_type=${selectedLoanType}`;
            } else if (uploadType === "PaymentFile") {
                apiUrl += "?pool_month=" + poolMonth;
            }

            const response = await apiClient.post(apiUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                responseType: 'blob',
                validateStatus: () => true
            });
            
            const contentType = response.headers['content-type'];

            if (response.status === 200) {                
                // Reset validation state and errors on successful upload
                setValidationState({
                    headers: [],
                    showFieldAliasModal: false,
                    missingPrimaryFields: [],
                    unmappedNonPrimaryFields: [],
                    primaryFieldsValid: false,
                    validationError: null
                });
                setCsvDownloadData(null);
                setError(null);
                setSelectedFile(null); // Clear selected file to encourage new upload
                const text = await response.data.text();
                const jsonData = JSON.parse(text);
                setSuccessMessage(jsonData.message || "File uploaded successfully! You can now upload another file.");
            } else if (response.status === 422 || response.status === 400) {
                // Handle validation errors - check content type to determine format
                const contentDisposition = response.headers['content-disposition'];
                const isCsv = (contentType && contentType.includes('text/csv')) ||
                    (contentDisposition && contentDisposition.includes('attachment'));

                if (isCsv) {
                    // CSV response with validation errors
                    try {
                    const csvText = await response.data.text();
                    const csvBlob = new Blob([csvText], { type: 'text/csv' });
                    setCsvDownloadData({
                        filename: 'validation_errors.csv',
                        data: csvBlob
                    });
                    
                    // Parse CSV to extract error details
                    const lines = csvText.split('\n').filter((line: string) => line.trim());
                    if (lines.length > 1) {
                        // Simple CSV parsing (handles quoted values)
                        const parseCSVLine = (line: string) => {
                            const result: string[] = [];
                            let current = '';
                            let inQuotes = false;
                            
                            for (let i = 0; i < line.length; i++) {
                                const char = line[i];
                                if (char === '"') {
                                    inQuotes = !inQuotes;
                                } else if (char === ',' && !inQuotes) {
                                    result.push(current.trim());
                                    current = '';
                                } else {
                                    current += char;
                                }
                            }
                            result.push(current.trim());
                            return result;
                        };
                        
                        const headers = parseCSVLine(lines[0]);
                        const errorDetails = {
                            type: 'Validation',
                            message: 'Upload validation failed',
                            details: {} as Record<string, string[]>
                        };
                        
                        // Parse each column's errors
                        for (let colIndex = 0; colIndex < headers.length; colIndex++) {
                            const columnName = headers[colIndex];
                            const columnErrors: string[] = [];
                            
                            for (let rowIndex = 1; rowIndex < lines.length; rowIndex++) {
                                const row = parseCSVLine(lines[rowIndex]);
                                const cellValue = row[colIndex]?.trim();
                                if (cellValue && cellValue !== '') {
                                    columnErrors.push(cellValue);
                                }
                            }
                            
                            if (columnErrors.length > 0) {
                                if (columnName === 'unmapped_loan_types') {
                                    errorDetails.details['Unmapped Loan Types'] = columnErrors;
                                } else if (columnName === 'unmapped_dpd_buckets') {
                                    errorDetails.details['Unmapped DPD Buckets'] = columnErrors;
                                } else if (columnName === 'unavailable_agency') {
                                    errorDetails.details['Unavailable Agencies'] = columnErrors;
                                } else if (columnName === 'unavailable_client_manager_username') {
                                    errorDetails.details['Unavailable Client Managers'] = columnErrors;
                                } else if (columnName === 'unavailable_pin_code') {
                                    errorDetails.details['Unavailable Pin Codes'] = columnErrors;
                                } else if (columnName === 'duplicate_client_reference_id') {
                                    errorDetails.details['Duplicate Client Reference IDs'] = columnErrors;
                                } else {
                                    errorDetails.details[columnName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())] = columnErrors;
                                }
                            }
                        }
                        
                        setValidationState(prev => ({ ...prev, validationError: errorDetails }));
                    }
                    
                    setError({
                        type: 'Validation',
                        message: 'Upload validation failed. Please check the error details by downloading the CSV file.',
                        details: []
                    });
                } catch (parseError) {
                    console.error('Error parsing CSV response:', parseError);
                    setError({
                        type: 'Parsing',
                        message: 'Upload validation failed. Please check by downloading the file for details.',
                        details: []
                    });
                }
            } else {
                // JSON response with structured error
                try {
                    const text = await response.data.text();
                    const parsedError = parseErrorResponse(text, 'Validation', 'Upload validation failed.');
                    setValidationState(prev => ({ ...prev, validationError: parsedError }));
                    setError(parsedError);
                } catch (parseError) {
                    console.error('Error parsing JSON response:', parseError);
                    setError({
                        type: 'Parsing',
                        message: 'Upload validation failed.',
                        details: []
                    });
                }
            }
        } else {
            // Handle other errors
            if (contentType && contentType.includes("application/json")) {
                const text = await response.data.text();
                const parsedError = parseErrorResponse(text, 'Upload', 'File upload failed.');
                setError(parsedError);
            } else {
                setError({
                    type: 'Upload',
                    message: 'File upload failed. Unknown error occurred.',
                    details: []
                });
            }
        }
        } catch (error: any) {
            if (error.response && error.response.data) {
                const parsedError = parseErrorResponse(error.response.data, 'Server', 'Server error occurred');
                setError(parsedError);
            } else if (error.request) {
                setError({
                    type: 'Network',
                    message: 'No response received from server',
                    details: []
                });
            } else {
                setError({
                    type: 'Unknown',
                    message: 'An unexpected error occurred',
                    details: error.message || []
                });
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleModalClose = () => {
        setSelectedFile(null);
        setError(null);
        setSuccessMessage(null);
        setIsUploading(false);
        setValidationState({
            headers: [],
            showFieldAliasModal: false,
            missingPrimaryFields: [],
            unmappedNonPrimaryFields: [],
            primaryFieldsValid: false,
            validationError: null
        });
        setCsvDownloadData(null);
        setSelectedLoanType('');
        setFieldAliases([]);
        onClose();
    };
    

    const handleFieldAliasComplete = () => {
        setValidationState(prev => ({ ...prev, showFieldAliasModal: false }));
        // Reload field aliases and restart validation
        loadFieldAliasesForLoanType().then(() => {
            validatePrimaryFields();
        });
    };

    return (
        <Modal
            open={open}
            onClose={handleModalClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
        >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography id="modal-title" variant="h6">
                        {label}
                    </Typography>
                    <IconButton onClick={handleModalClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {uploadType === "PaymentFile" && <MonthYearSelector onDateSelection={setPoolMonth}/>}
                
                {/* Loan Type Selector for Case File Upload */}
                { (uploadType === "CaseUpload" || uploadType === "BulkUpdate") && (
                    <Box mt={2} mb={2}>
                        <FormControl fullWidth required>
                            <InputLabel>Loan Type</InputLabel>
                            <Select
                                value={selectedLoanType}
                                label="Loan Type"
                                onChange={(e) => setSelectedLoanType(e.target.value)}
                            >
                                {loanTypes.map((loanType) => (
                                    <MenuItem key={loanType} value={loanType}>
                                        {loanType}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedLoanType && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Field aliases and validations will be applied for <strong>{selectedLoanType}</strong> loan type.
                            </Typography>
                        )}
                    </Box>
                )}

                <Box mt={2} mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Select an Excel file (.xlsx) with 'Sheet1' worksheet
                    </Typography>
                </Box>

                {uploadFormat && (
                    <Box mb={2}>
                        <Typography>File Upload Format:</Typography>
                        <Typography>{uploadFormat}</Typography>
                    </Box>
                )}

                {/* File Input */}
                <Box mt={1} mb={2}>
                    <TextField
                        type="file"
                        fullWidth
                        slotProps={{ htmlInput: { accept: '.xlsx' } }}
                        onChange={handleFileChange}
                        sx={{
                            '& input': {
                                padding: '8px',
                            },
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                        }}
                    />
                </Box>

                {/* Display selected file name */}
                {selectedFile && (
                    <Box mb={2}>
                        <Typography variant="body1" fontWeight="bold">
                            Selected file: {selectedFile.name}
                        </Typography>
                    </Box>
                )}

                {/* Loading Message During Validation */}
                {isValidating && selectedFile && (
                    <Box mt={2} mb={2} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Validating primary fields...
                        </Typography>
                    </Box>
                )}

                {/* Error Display */}
                {(error || validationState.validationError) && (
                    <Box mb={2}>
                        <ErrorDisplay 
                            error={validationState.validationError || error}
                            csvDownloadData={csvDownloadData}
                            loanType={selectedLoanType}
                        />
                        
                        {/* Configuration buttons for specific error types */}
                        {error && (error.message.includes('No primary field aliases found') || error.message.includes('Missing required field aliases')) && (
                            <Box mt={2}>
                                <Button 
                                    variant="outlined" 
                                    size="small" 
                                    onClick={() => setValidationState(prev => ({ ...prev, showFieldAliasModal: true }))}
                                >
                                    Configure Field Aliases
                                </Button>
                            </Box>
                        )}
                        
                        {validationState.missingPrimaryFields.length > 0 && (
                            <Box mt={1}>
                                <Typography variant="body2" fontWeight="bold">
                                    Missing Required Column Headers:
                                </Typography>
                                <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                                    {validationState.missingPrimaryFields.map(alias => (
                                        <li key={alias}>{alias}</li>
                                    ))}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Your Excel file must contain these column headers, or you can configure field aliases to map your existing headers to these required fields.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    size="small" 
                                    sx={{ mt: 1 }}
                                    onClick={() => setValidationState(prev => ({ ...prev, showFieldAliasModal: true }))}
                                >
                                    Configure Field Aliases
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Success Message */}
                {successMessage && (
                    <Box mb={2}>
                        <Alert severity="success">
                            {successMessage}
                        </Alert>
                    </Box>
                )}

                {/* Warning Message for Unmapped Non-Primary Fields */}
                {validationState.primaryFieldsValid && validationState.unmappedNonPrimaryFields.length > 0 && (
                    <Box mb={2}>
                        <Alert severity="warning">
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                Some columns will be ignored during upload:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                These column headers are not configured as standard fields or field aliases. 
                                They will be ignored during upload. You can still proceed, or configure field aliases to map these columns to standard fields or create new custom fields.
                            </Typography>
                            <Box component="ul" sx={{ mt: 1, pl: 3, mb: 1 }}>
                                {validationState.unmappedNonPrimaryFields.map(field => (
                                    <li key={field}>{field}</li>
                                ))}
                            </Box>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                sx={{ mt: 1 }}
                                onClick={() => setValidationState(prev => ({ ...prev, showFieldAliasModal: true }))}
                            >
                                Configure Field Aliases
                            </Button>
                        </Alert>
                    </Box>
                )}


                {/* Action Buttons */}
                <Box display="flex" justifyContent="flex-end" gap={2}>
                    {!validationState.primaryFieldsValid ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={validatePrimaryFields}
                            disabled={!selectedFile || isValidating || ((uploadType === "CaseUpload" || uploadType === "BulkUpdate") && !selectedLoanType)}
                            sx={{
                                bgcolor: '#1976d2',
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': {
                                    bgcolor: '#1565c0',
                                },
                            }}
                        >
                            {isValidating ? <CircularProgress size={24} /> : 'Validate File'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleActualUpload}
                            disabled={isUploading}
                            sx={{
                                fontWeight: 'bold'
                            }}
                        >
                            {isUploading ? <CircularProgress size={24} /> : 'Upload File'}
                        </Button>
                    )}
                </Box>
                
                
                {/* Field Alias Modal */}
                {validationState.showFieldAliasModal && selectedLoanType && (
                    <ConfigureFieldAliasModal
                        open={validationState.showFieldAliasModal}
                        onClose={handleFieldAliasComplete}
                        initialFieldAliases={fieldAliases}
                        loanType={selectedLoanType}
                        onSave={handleFieldAliasComplete}
                    />
                )}
            </Box>
        </Modal>
    );
};

export default UploadCaseFileComponent;
